import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { getPoolDetail } from "@/api/mockData";
import { t, formatRub, formatGNK, type Lang } from "@/lib/i18n";
import { PoolDeposit } from "./PoolDeposit";

interface PoolDetailProps {
  poolId: string;
  lang: Lang;
  onBack: () => void;
}

export function PoolDetail({ poolId, lang, onBack }: PoolDetailProps) {
  const pool = getPoolDetail(poolId);
  const [showDeposit, setShowDeposit] = useState(false);
  const [tab, setTab] = useState<"info" | "members" | "history">("info");
  const progress = (pool.collectedAmount / pool.targetAmount) * 100;

  if (showDeposit) {
    return <PoolDeposit pool={pool} lang={lang} onBack={() => setShowDeposit(false)} />;
  }

  return (
    <div className="space-y-7 animate-fade-in">
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors pt-3 group"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="group-hover:-translate-x-0.5 transition-transform">
          <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
        </svg>
        <span className="text-xs font-medium tracking-wide">{t("pool.back", lang)}</span>
      </button>

      {/* Pool Header Card */}
      <div className="glass-card-featured rounded-2xl overflow-hidden">
        <div className="h-[3px]" style={{ background: 'linear-gradient(90deg, rgba(255,255,255,0.12), rgba(255,255,255,0.03), transparent)' }} />
        <div className="p-5 space-y-5">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div
                className="w-14 h-14 rounded-xl flex flex-col items-center justify-center"
                style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: '#888888', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                <span className="text-[11px] font-display font-black">GPU</span>
                <span className="text-[11px] font-mono-num font-bold opacity-70">×{pool.gpuCount}</span>
              </div>
              {pool.status === "mining" && (
                <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-white/50 border-2 border-background" />
              )}
            </div>
            <div>
              <h1 className="font-display text-2xl font-black tracking-tight">{pool.name}</h1>
              <p className="text-sm text-muted-foreground mt-0.5">{pool.gpuModel}</p>
            </div>
          </div>

          {/* Progress */}
          <div>
            <div className="flex justify-between items-baseline mb-2">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">{t("pools.collected", lang)}</span>
              <span className="font-mono-num text-sm font-bold text-foreground">{Math.round(progress)}%</span>
            </div>
            <div className="h-3 bg-muted/40 rounded-full overflow-hidden">
              <div
                className="progress-bar h-full transition-all duration-1000"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex justify-between text-[10px] text-muted-foreground mt-1.5 font-mono-num">
              <span>{formatRub(pool.collectedAmount)}</span>
              <span className="font-semibold text-foreground/70">{formatRub(pool.targetAmount)}</span>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-2">
            <QuickStat label={t("pools.hashrate", lang)} value={pool.hashrate} />
            <QuickStat label={t("pools.yield", lang)} value={`${pool.monthlyYield}%`} accent="green" />
            <QuickStat label={t("pools.participants", lang)} value={String(pool.participantCount)} />
          </div>
        </div>
      </div>

      {/* User Stats (if participating) */}
      {pool.userShare > 0 && (
        <div className="grid grid-cols-2 gap-3">
          <div className="glass-card rounded-2xl p-5 relative overflow-hidden">
            <p className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground/60 font-medium">{t("pool.yourShare", lang)}</p>
            <p className="text-2xl font-black font-mono-num text-foreground mt-2">{pool.userShare}%</p>
            <p className="text-[10px] text-muted-foreground/50 font-mono-num mt-1">{formatRub(pool.userInvested)}</p>
          </div>
          <div className="glass-card rounded-2xl p-5 relative overflow-hidden">
            <p className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground/60 font-medium">{t("pool.yourEarnings", lang)}</p>
            <p className="text-2xl font-black font-mono-num text-primary mt-2">{formatGNK(pool.userEarnings)}</p>
            <p className="text-[10px] text-muted-foreground/50 mt-1">GNK</p>
          </div>
        </div>
      )}

      {/* Action Button — prominent CTA */}
      {pool.status === "collecting" && (
        <button
          onClick={() => setShowDeposit(true)}
          className="w-full h-[56px] rounded-2xl cta-primary font-display flex items-center justify-center gap-2.5"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 5v14" /><path d="M5 12h14" />
          </svg>
          {t("pool.deposit", lang)}
        </button>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-secondary/30 rounded-xl p-1 border border-white/[0.03]">
        {(["info", "members", "history"] as const).map((key) => {
          const labels = { info: t("pool.info", lang), members: t("pool.members", lang), history: t("pool.history", lang) };
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex-1 py-2.5 text-[11px] font-semibold rounded-lg transition-all duration-300 ${
                tab === key ? "bg-card text-foreground shadow-md shadow-black/20 border border-white/[0.04]" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {labels[key]}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {tab === "info" && (
        <Card className="glass-card border-0 rounded-2xl">
          <CardContent className="p-5 space-y-3">
            <p className="text-sm text-muted-foreground leading-relaxed">{pool.description}</p>
            <div className="grid grid-cols-2 gap-y-3 pt-2">
              <InfoRow label={t("pools.gpu", lang)} value={pool.gpuModel} />
              <InfoRow label="GPU ×" value={String(pool.gpuCount)} />
              <InfoRow label={t("pools.hashrate", lang)} value={pool.hashrate} />
              <InfoRow label={t("pools.yield", lang)} value={`${pool.monthlyYield}%`} />
              {pool.status === "mining" && (
                <>
                  <InfoRow label={t("pool.totalMined", lang)} value={`${formatGNK(pool.totalMinedGNK)} GNK`} />
                  <InfoRow label={t("pool.dailyReward", lang)} value={`${pool.dailyRewardGNK} GNK`} />
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {tab === "members" && (
        <Card className="glass-card border-0 rounded-2xl overflow-hidden">
          <CardContent className="p-0">
            {pool.members.map((m, i) => (
              <div
                key={m.id}
                className={`flex items-center gap-3 px-4 py-3 ${
                  i !== pool.members.length - 1 ? "border-b border-white/[0.03]" : ""
                }`}
              >
                <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold text-foreground/70">
                  {m.avatar}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">{m.name}</p>
                  <p className="text-[10px] text-muted-foreground font-mono-num">{formatRub(m.invested)}</p>
                </div>
                <span className="text-xs font-mono-num text-foreground/80 font-semibold">{m.share}%</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {tab === "history" && (
        <Card className="glass-card border-0 rounded-2xl overflow-hidden">
          <CardContent className="p-0">
            {pool.rewardHistory.map((entry, i) => (
              <div
                key={i}
                className={`flex items-center justify-between px-4 py-3 ${
                  i !== pool.rewardHistory.length - 1 ? "border-b border-white/[0.03]" : ""
                }`}
              >
                <div>
                  <p className="text-sm">{entry.date}</p>
                  <p className="text-[10px] text-muted-foreground">{entry.pool}</p>
                </div>
                <span className="font-mono-num text-sm font-semibold text-primary">+{formatGNK(entry.amount)} GNK</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function QuickStat({ label, value, accent }: { label: string; value: string; accent?: "green" }) {
  return (
    <div className={`text-center rounded-xl py-3 px-2 ${accent === "green" ? "bg-chart-3/6 border border-chart-3/10" : "bg-secondary/20 border border-white/[0.03]"}`}>
      <p className="text-[8px] uppercase tracking-widest text-muted-foreground/60 font-medium">{label}</p>
      <p className={`text-[15px] font-mono-num font-bold mt-1 ${accent === "green" ? "text-chart-3" : "text-foreground/75"}`}>{value}</p>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className="text-sm font-medium mt-0.5">{value}</p>
    </div>
  );
}
